import React, { forwardRef } from 'react';
import cn from 'classnames';

export type MPFButtonVariant =
  | 'primary'
  | 'secondary'
  /** Fundo suave da cor de marca (primary-container), sem borda. */
  | 'tonal'
  | 'outline'
  | 'ghost'
  /**
   * Sem cor/fundo próprio — só a classe estrutural base (`mpf-button`).
   * Use quando o visual já vem de uma className bespoke existente e o
   * MPFButton está sendo adotado só pelo comportamento (ref, disabled,
   * aria-busy, bloqueio de clique em links desabilitados etc.).
   */
  | 'unstyled';
export type MPFButtonSize = 'm' | 'l' | 'xl';

export interface MPFButtonProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'as' | 'onClick'
  > {
  variant?: MPFButtonVariant;
  size?: MPFButtonSize;
  fullWidth?: boolean;
  /** Usado internamente pelo MPFIconButton. */
  shape?: 'square' | 'circle';
  loading?: boolean;
  as?: 'button' | 'a' | React.ElementType;
  href?: string;
  /**
   * Só faz sentido com `as` sendo um link (ex.: `UniversalLink` do Volto, que
   * lê esta prop para decidir o `target`). É repassada junto com o resto das
   * props, então não use com o `as` padrão de `button`.
   */
  openLinkInNewTab?: boolean;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
}

export const MPFButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  MPFButtonProps
>(function MPFButton(
  {
    variant = 'primary',
    size = 'l',
    fullWidth = false,
    shape,
    loading = false,
    as = 'button',
    href,
    className,
    disabled,
    children,
    onClick,
    type,
    ...rest
  },
  ref,
) {
  const isLink = as !== 'button';
  const isDisabled = Boolean(disabled || loading);

  const isUnstyled = variant === 'unstyled';

  const classes = cn(
    !isUnstyled && 'mpf-button',
    !isUnstyled && `mpf-button--${size}`,
    !isUnstyled && `mpf-button--${variant}`,
    {
      'mpf-button--circle': shape === 'circle',
      'mpf-button--square': shape === 'square',
      'mpf-button--block': fullWidth,
    },
    className,
  );

  const content = (
    <>
      {loading && (
        <span className="mpf-loading mpf-loading-spinner" aria-hidden="true" />
      )}
      {children}
    </>
  );

  if (isLink) {
    const Component: React.ElementType = as === 'a' ? 'a' : as;
    const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
      if (isDisabled) {
        event.preventDefault();
        return;
      }
      onClick?.(event);
    };

    return (
      <Component
        ref={ref}
        href={href}
        className={classes}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading || undefined}
        onClick={handleClick}
        {...rest}
      >
        {content}
      </Component>
    );
  }

  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type={type ?? 'button'}
      className={classes}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      onClick={onClick}
      {...rest}
    >
      {content}
    </button>
  );
});

export default MPFButton;
